import { PERMISSIONS } from '@ibe/constants';
import { DataTable } from '@ibe/ui';
import { z } from 'zod';

import { PermissionGuard } from '../../auth/permission-guard.js';
import { errorTextFor } from '../../lib/api/error-message.js';
import { useApiList } from '../../lib/api/hooks.js';

/**
 * What has been done to one record.
 *
 * Reads the platform audit log narrowed to this entity, rather than a per-module
 * audit route: the log already owns its permission question, and twelve routes
 * over one table would each have to answer it again.
 *
 * Rendered inside a `PermissionGuard` with no fallback — someone without
 * `Audit.Read` sees a detail screen with no audit section, rather than a section
 * telling them they may not read it. The absence is the honest answer; the
 * refusal would be an invitation.
 */

const ENTRIES_PER_PAGE = 10;

const auditEntry = z.object({
  id: z.string(),
  occurredAt: z.string(),
  actor: z.string(),
  action: z.string(),
  entity: z.string(),
  correlationId: z.string(),
});

type AuditEntryRow = z.infer<typeof auditEntry>;

function formatMoment(value: string): string {
  return new Date(value).toLocaleString('en-AU', { timeZone: 'UTC' });
}

const columns = [
  {
    id: 'occurredAt',
    header: 'When (UTC)',
    fields: ['occurredAt'],
    cell: (row: AuditEntryRow) => formatMoment(row.occurredAt),
  },
  { id: 'action', header: 'Action', fields: ['action'], cell: (row: AuditEntryRow) => row.action },
  { id: 'actor', header: 'By', fields: ['actor'], cell: (row: AuditEntryRow) => row.actor },
];

export interface AuditTrailPanelProps {
  readonly entityType: string;
  readonly entityId: string;
}

export function AuditTrailPanel({ entityType, entityId }: AuditTrailPanelProps): React.JSX.Element {
  return (
    <PermissionGuard permission={PERMISSIONS.AUDIT_READ}>
      <AuditTrail entityType={entityType} entityId={entityId} />
    </PermissionGuard>
  );
}

function AuditTrail({ entityType, entityId }: AuditTrailPanelProps): React.JSX.Element {
  const query = useApiList({
    resource: 'audit',
    path: '/v1/audit',
    schema: auditEntry,
    params: {
      page: 1,
      pageSize: ENTRIES_PER_PAGE,
      scope: { entityType, entityId },
    },
  });

  return (
    <section className="space-y-3">
      <h2 className="text-body font-semibold">Activity</h2>
      <DataTable
        caption="Activity"
        columns={columns}
        rows={query.data?.items ?? []}
        rowKey={(row: AuditEntryRow) => row.id}
        loading={query.isPending}
        error={errorTextFor(query.error)}
        onRetry={() => {
          void query.refetch();
        }}
        emptyTitle="No recorded activity yet"
        emptyDescription="Changes to this record appear here, newest first."
      />
    </section>
  );
}
